import { useEffect, useRef, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import type { TranslationKeys } from '../../i18n/types.js';
import { ModeSwitch } from '../ModeSwitch';
import { Button } from '../ui/button';
import { ImageGalleryButton } from '../images/ImageGalleryButton';
import { Input } from '../ui/input';

export interface StudioTopbarProps {
  readonly campaignLabel: string;
  /** No name of its own: the header says so and the rename starts empty. */
  readonly campaignUnnamed?: boolean;
  readonly sessionLabel: string;
  readonly t: (key: keyof TranslationKeys) => string;
  readonly onExitStudio: () => void;
  /** Клик по подсказке «Ctrl K» — открыть палитру (макет: .palette). */
  readonly onOpenPalette?: () => void;
  /** The campaign's name is edited here too (the lobby's «Переименовать»); absent — plain text. */
  readonly onRenameCampaign?: (name: string) => void;
  /** The campaign calendar button (the surface builds it: it needs the session). */
  readonly calendar?: ReactNode;
  /** «У тебя / У игроков» — once for the whole Studio (StudioMapStrip). */
  readonly mapStrip?: ReactNode;
}

export function StudioTopbar({
  campaignLabel: campaignName,
  campaignUnnamed = false,
  sessionLabel,
  t,
  onExitStudio,
  onOpenPalette,
  onRenameCampaign,
  calendar,
  mapStrip,
}: StudioTopbarProps): ReactElement {
  const campaignLabel = campaignUnnamed ? t('studio.topbar.unnamed') : campaignName;
  const [renaming, setRenaming] = useState<boolean>(false);
  const [draft, setDraft] = useState<string>(campaignUnnamed ? '' : campaignName);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect((): void => {
    if (renaming) inputRef.current?.select();
  }, [renaming]);
  const commit = (): void => {
    const name = draft.trim();
    setRenaming(false);
    if (name !== '' && (campaignUnnamed || name !== campaignName)) onRenameCampaign?.(name);
  };
  // «К столу» — только выход (всегда активна, иначе мастер заперт в Studio).
  // Карту игрокам он не активирует ни сразу, ни отложенным намерением: выбор
  // бита — подготовка, а не текущий эпизод игры (пакет 2). Показ карты
  // игрокам — отдельное явное действие.

  return (
    <div
      className="studio-topbar flex h-12 flex-none items-center justify-between border-b border-line bg-panel px-4"
      role="banner"
    >
      <nav
        className="studio-topbar__breadcrumbs flex items-center gap-2 text-ui text-muted-foreground"
        aria-label="breadcrumb"
      >
        {onRenameCampaign === undefined ? (
          <span className="studio-topbar__crumb font-medium text-fg">{campaignLabel}</span>
        ) : renaming ? (
          <Input
            ref={inputRef}
            className="studio-topbar__crumb h-auto w-48 border-hl/50 bg-surface px-1.5 py-0.5 text-ui font-medium text-fg pointer-coarse:h-auto"
            data-testid="studio-campaign-name-input"
            aria-label={t('studio.topbar.renameCampaign')}
            maxLength={30}
            value={draft}
            onChange={(e): void => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e): void => {
              if (e.key === 'Enter') commit();
              if (e.key === 'Escape') setRenaming(false);
            }}
          />
        ) : (
          <Button
            type="button"
            variant="ghost"
            size="inline"
            className={`studio-topbar__crumb px-1.5 py-0.5 font-medium hover:bg-panel-2 hover:text-fg ${campaignUnnamed ? 'italic text-dim' : 'text-fg'}`}
            data-testid="studio-campaign-name"
            title={t('studio.topbar.renameCampaign')}
            onClick={(): void => {
              setDraft(campaignUnnamed ? '' : campaignName);
              setRenaming(true);
            }}
          >
            {campaignLabel}
          </Button>
        )}
        {sessionLabel !== '' ? (
          <>
            <span className="studio-topbar__separator text-dim" aria-hidden="true">
              {'\u00B7'}
            </span>
            <span className="studio-topbar__crumb">{sessionLabel}</span>
          </>
        ) : null}
        <span className="studio-topbar__separator text-dim" aria-hidden="true">
          {'\u00B7'}
        </span>
        <span
          className="studio-topbar__crumb studio-topbar__crumb--current rounded-full border border-hl/50 bg-hl/10 px-2.5 py-0.5 text-pill font-semibold uppercase tracking-wider text-hl"
          aria-current="page"
        >
          {t('studio.breadcrumb.prep')}
        </span>
      </nav>
      {mapStrip !== undefined ? <div className="ml-4 flex min-w-0 flex-[0_1_auto]">{mapStrip}</div> : null}
      {onOpenPalette !== undefined ? (
        <button
          type="button"
          className="studio-topbar__palette mx-4 flex max-w-[420px] flex-1 items-center gap-2 rounded-md border border-line bg-background px-3 py-1 text-ui text-dim hover:border-hl/40 hover:text-muted-foreground"
          data-testid="studio-palette-open"
          onClick={onOpenPalette}
        >
          <kbd className="rounded border border-line bg-panel-2 px-1.5 py-px font-mono text-label text-muted-foreground">
            Ctrl K
          </kbd>
          <span className="truncate">{t('studio.palette.hint')}</span>
        </button>
      ) : null}
      <div className="studio-topbar__actions flex items-center gap-3">
        {/* Group Q: the generated pictures (no gear in Подготовка — the icon sits with the top bar actions). */}
        <ImageGalleryButton placement="topbar" />
        {calendar}
        <ModeSwitch current="prep" accent="studio" t={t} playClassName="studio-topbar__to-table" onPrep={(): void => {}} onPlay={onExitStudio} />
      </div>
    </div>
  );
}
